import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["open", "onOpenChange", "onOpenStart", "_bodyElement", "placement", "autoPlacement", "use", "arrowSize", "animateOnMount", "animateOnToggle", "closeOnOutsideClick", "outsideClickIgnoreElementsList", "closeOnTargetLeave", "openOnTargetEnter", "onOpenComplete", "onCloseComplete", "onFocusLeave", "onClick", "showCloseButton", "zIndex", "arrowColor", "children", "Content", "content", "target", "shouldCloseOnClick", "offset"];
import styled, { useTheme } from 'foundations-theming/utils';
import { Children, cloneElement, isValidElement, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FloatingArrow, FloatingFocusManager, arrow, autoUpdate, flip, offset, shift, useDismiss, useFloating, useInteractions, limitShift, useTransitionStyles } from '@floating-ui/react';
import FloatingPortalWrapper from './FloatingPortalWrapper';
import { syntheticEvent } from '../../utils/SyntheticEvent/SyntheticEvent';
import { ChildrenWrapper } from '../ChildrenWrapper/ChildrenWrapper';
import { findDOMNode } from 'react-dom';
import { FLOATING_UI_PLACEMENTS_MAP, PLACEMENTS_FLOATING_UI_MAP } from '../Tooltip/placementConstants';
import { isFragment } from 'react-is';
import devLogger from 'react-utils/devLogger';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { PopoverProvider } from './PopoverContext';
import { PopoverHeader } from './PopoverHeader';
import { PopoverBody } from './PopoverBody';
import { PopoverFooter } from './PopoverFooter';
import { rgba } from '../../utils/color';
import { StyledText } from '../../../components/Text/Text';
import { POPOVER_ANIMATION_DURATION } from '../../../constants/times';
import { ModalTransitionContext } from '../Modal/ModalTransitionContext';
import { getDefaultNode } from '../../utils/RootNode';
import { JUPITER_LAYER } from '../../constants';
import { useRTEContext } from '../../context/RTEContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const animationDuration = parseInt(POPOVER_ANIMATION_DURATION, 10);
const StyledFloatingContainer = styled.div.withConfig({
  displayName: "Popover__StyledFloatingContainer"
})(({
  $zIndex,
  $floatingStyles
}) => {
  return Object.assign({}, $floatingStyles, {
    zIndex: $zIndex // The z-index needs to be in the root-level popover element
  });
});
export const getPopoverContainerBorderStyles = theme => ({
  borderBlockWidth: theme.borderWidth['100'],
  borderInlineWidth: theme.borderWidth['100'],
  borderBlockStyle: 'solid',
  borderInlineStyle: 'solid',
  borderBlockColor: theme.color['border-transitional-core-subtle'],
  borderInlineColor: theme.color['border-transitional-core-subtle'],
  boxShadow: theme.boxShadow['400']
});
const StyledPopoverContainer = styled.div.withConfig({
  displayName: "Popover__StyledPopoverContainer"
})(({
  $cssStyles,
  $use,
  theme
}) => {
  const {
    inlineSize
  } = $cssStyles;
  return Object.assign({
    maxInlineSize: '100vw',
    position: 'relative'
  }, $use !== 'unstyled' ? {
    borderStartStartRadius: theme.borderRadius['transitional-container-alt'],
    borderStartEndRadius: theme.borderRadius['transitional-container-alt'],
    borderEndEndRadius: theme.borderRadius['transitional-container-alt'],
    borderEndStartRadius: theme.borderRadius['transitional-container-alt']
  } : undefined, $use === 'default' || $use === 'longform' ? Object.assign({}, getPopoverContainerBorderStyles(theme)) : undefined, $use === 'default' || $use === 'longform' || $use === 'shepherd' ? {
    backgroundColor: theme.color['fill-surface-default']
  } : undefined, $use === 'longform' ? {
    lineHeight: '1.5',
    // Relative value that doesn't need a token
    maxInlineSize: inlineSize || '280px',
    fontSize: theme.typography['detail-100'].fontSize,
    '& ul, & ol': {
      paddingInlineStart: theme.space['400']
    }
  } : undefined, $use === 'shepherd' ? {
    boxShadow: `0 0 0 ${theme.borderWidth['100']} ${rgba(theme.color['fill-accent-neutral-hover'], 0.3)}, ${theme.boxShadow['400']}`
  } : undefined, $use === 'tooltip' || $use === 'tooltip-danger' ? {
    fontSize: theme.typography['detail-100'].fontSize,
    backgroundColor: $use === 'tooltip' ? theme.color['fill-accent-neutral-default'] : theme.color['fill-alert-default'],
    color: theme.color['text-core-on-fill-default'],
    maxInlineSize: inlineSize || '232px',
    [`& ${StyledText}`]: {
      color: theme.color['text-core-on-fill-default']
    }
  } : undefined, {
    // Applies a start border radius to the first child of the popover container to prevent overflow
    '& > :first-child': {
      borderStartStartRadius: 'inherit',
      borderStartEndRadius: 'inherit'
    },
    // Applies an end border radius to the last child (1 before the arrow) of the popover container to prevent overflow
    '& > :nth-last-child(2)': {
      borderEndEndRadius: 'inherit',
      borderEndStartRadius: 'inherit'
    }
  }, $cssStyles);
});
const ARROW_SIZE_MAP = {
  none: {
    distance: '100',
    width: 0,
    height: 0
  },
  small: {
    distance: '400',
    width: 22,
    height: 11
  },
  medium: {
    distance: '500',
    width: 28,
    height: 14
  }
};
export const Popover = _ref => {
  let {
      open = false,
      onOpenChange,
      onOpenStart,
      _bodyElement,
      placement = 'top',
      autoPlacement = true,
      use = 'default',
      arrowSize,
      animateOnMount = false,
      animateOnToggle = arrowSize !== 'none',
      closeOnOutsideClick = true,
      outsideClickIgnoreElementsList = [],
      closeOnTargetLeave = false,
      openOnTargetEnter = false,
      onOpenComplete,
      onCloseComplete,
      onFocusLeave,
      onClick,
      showCloseButton = false,
      zIndex = parseInt(JUPITER_LAYER, 10),
      arrowColor,
      children,
      Content,
      content,
      target,
      shouldCloseOnClick,
      offset: additionalOffset = '0'
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  // Creates refs for callback props to prevent infinite loops when the props change
  const onOpenChangeRef = useRef(onOpenChange);
  const onOpenStartRef = useRef(onOpenStart);
  const onOpenCompleteRef = useRef(onOpenComplete);
  const onCloseCompleteRef = useRef(onCloseComplete);
  const onFocusLeaveRef = useRef(onFocusLeave);
  const contentOpenRef = useRef(false);
  const isFirstRenderRef = useRef(true);
  const animationSourceRef = useRef(open ? 'mount' : 'toggle');

  // This is necessary to avoid duplicating tooltips inside Tabs (https://git.hubteam.com/HubSpot/foundations-components/issues/3964)
  const {
    transitioning: inModalTransition
  } = useContext(ModalTransitionContext);

  // The default value needs to be `true` to ensure that the popover opens when `open` and `closeOnTargetLeave` are `true` on mount.
  // See https://git.hubteam.com/HubSpot/foundations-components/issues/3451
  const [isReferenceVisible, setIsReferenceVisible] = useState(true);
  const [referenceElement, setReferenceElement] = useState(null);
  const contentOpen = open && !inModalTransition &&
  // Ensure that target/reference element is up to date
  // https://git.hubteam.com/HubSpot/UIComponents/issues/9667
  !!(target || referenceElement);

  // Use animateOnMount for mount-time opening, animateOnToggle for user interactions
  const isAnimated = animationDuration > 0 && (contentOpen && animationSourceRef.current === 'mount' ? animateOnMount : animateOnToggle);
  useLayoutEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);
  useLayoutEffect(() => {
    onOpenStartRef.current = onOpenStart;
  }, [onOpenStart]);
  useLayoutEffect(() => {
    onOpenCompleteRef.current = onOpenComplete;
  }, [onOpenComplete]);
  useLayoutEffect(() => {
    onCloseCompleteRef.current = onCloseComplete;
  }, [onCloseComplete]);
  useLayoutEffect(() => {
    onFocusLeaveRef.current = onFocusLeave;
  }, [onFocusLeave]);
  useLayoutEffect(() => {
    contentOpenRef.current = contentOpen;
  }, [contentOpen]);
  const [transitionStatus, setTransitionStatus] = useState(contentOpen ? 'OPENING' : 'CLOSED');
  const [containerElement, setContainerElement] = useState(null);
  const theme = useTheme();
  const arrowRef = useRef(null);
  const defaultArrowPadding = useMemo(() => parseInt(theme.space['100'], 10), [theme]);
  const [arrowPadding, setArrowPadding] = useState(defaultArrowPadding);
  const {
    domProps,
    tokenProps,
    callbackProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const rteContext = useRTEContext();
  const arrowSizeDimensions = ARROW_SIZE_MAP[arrowSize || (use === 'tooltip' || use === 'tooltip-danger' ? 'small' : 'medium')];
  const showArrowStroke = use === 'default' || use === 'longform' || use === 'shepherd';

  // when you pass margin to useMapTokenPropsToCSS, it separates the margin into marginBlockEnd, marginBlockStart, marginInlineEnd, marginInlineStart
  // so we just pass the offset as marginBlockEnd
  const {
    marginBlockEnd = '0'
  } = useMapTokenPropsToCSS({
    marginBlockEnd: additionalOffset
  });

  // extract the number from the marginBlockEnd that's in px
  const computedOffset = parseInt(String(marginBlockEnd).replace('px', ''), 10);
  const [popoverContainerElement, setPopoverContainerElement] = useState(null);
  const {
    refs,
    floatingStyles,
    context,
    placement: currentPlacement
  } = useFloating({
    placement: FLOATING_UI_PLACEMENTS_MAP[placement],
    open: contentOpen,
    onOpenChange: newIsOpen => {
      var _onOpenChangeRef$curr;
      (_onOpenChangeRef$curr = onOpenChangeRef.current) === null || _onOpenChangeRef$curr === void 0 || _onOpenChangeRef$curr.call(onOpenChangeRef, syntheticEvent(newIsOpen));
    },
    middleware: [offset(parseInt(theme.space[arrowSizeDimensions.distance], 10) + computedOffset), autoPlacement ? flip({
      fallbackAxisSideDirection: 'start'
    }) : undefined, autoPlacement ? shift({
      limiter: limitShift()
    }) : undefined, arrow({
      element: arrowRef,
      padding: arrowPadding
    })],
    whileElementsMounted: autoUpdate,
    elements: {
      reference: target // The reference element defaults to the child unless the target prop is used
    }
  });
  const {
    isMounted,
    styles: animationStyles
  } = useTransitionStyles(context, {
    duration: isAnimated ? animationDuration : 0,
    common: ({
      side
    }) => ({
      transformOrigin: {
        top: 'bottom',
        bottom: 'top',
        left: 'right',
        right: 'left'
      }[side]
    }),
    initial: {
      opacity: 0,
      transform: 'scale(0.75)'
    },
    open: {
      opacity: 1,
      transform: 'scale(1)'
    },
    close: {
      opacity: 0,
      transform: 'scale(0.75)'
    }
  });
  const popoverId = useUniqueId(`uiopenpopup-`);
  const currentPlacementSide = currentPlacement.split('-')[0];
  const oppositeCurrentPlacementSide = currentPlacementSide === 'top' ? 'bottom' : currentPlacementSide === 'right' ? 'left' : currentPlacementSide === 'bottom' ? 'top' : 'right';
  const computedArrowColor = useMemo(() => {
    var _arrowColor$mappedPla;
    if (!arrowColor) {
      return undefined;
    }
    if (typeof arrowColor === 'string') {
      return arrowColor;
    }
    const mappedPlacement = PLACEMENTS_FLOATING_UI_MAP[currentPlacement];
    return (_arrowColor$mappedPla = arrowColor[mappedPlacement]) !== null && _arrowColor$mappedPla !== void 0 ? _arrowColor$mappedPla : arrowColor.default;
  }, [arrowColor, currentPlacement]);

  /**
   * Update arrow padding based on the container's computed border radius.
   * Only corner placements (left-start, left-end, right-start, right-end) are affected.
   * useLayoutEffect ensures this runs synchronously before paint, preventing visual glitches for popovers open by default.
   */
  useLayoutEffect(() => {
    if (!popoverContainerElement) {
      return;
    }

    // Map of placements to the border radius property that might cause clipping
    const placementToBorderRadius = {
      'left-start': 'borderStartEndRadius',
      // Arrow on right side near top-right corner
      'left-end': 'borderEndEndRadius',
      // Arrow on right side near bottom-right corner
      'right-start': 'borderStartStartRadius',
      // Arrow on left side near top-left corner
      'right-end': 'borderEndStartRadius' // Arrow on left side near bottom-left corner
    };
    const borderRadiusProperty = placementToBorderRadius[currentPlacement];
    if (!borderRadiusProperty) {
      // Reset to default padding for non-corner placements
      setArrowPadding(defaultArrowPadding);
      return;
    }
    const computedStyle = window.getComputedStyle(popoverContainerElement);

    // getComputedStyle returns computed pixel values, regardless of the original unit (rem, em, %, etc.)
    const borderRadius = parseInt(computedStyle[borderRadiusProperty], 10);
    if (!isNaN(borderRadius)) {
      setArrowPadding(Math.max(defaultArrowPadding, borderRadius));
    }
  }, [popoverContainerElement, currentPlacement, defaultArrowPadding]);

  // Keeps track of the visibility of the reference element
  useEffect(() => {
    if (openOnTargetEnter || closeOnTargetLeave && contentOpen) {
      const referenceDOMElement = refs.domReference.current;
      const observer = new IntersectionObserver(([domReferenceElement]) => {
        setIsReferenceVisible(domReferenceElement.isIntersecting);
      });
      if (referenceDOMElement) {
        observer.observe(referenceDOMElement);
      }
      return () => {
        if (referenceDOMElement) {
          observer.unobserve(referenceDOMElement);
        }
      };
    }
  }, [openOnTargetEnter, closeOnTargetLeave, contentOpen, refs.domReference]);

  // Updates the open state based on the viewport visibility of the reference element
  useEffect(() => {
    // This logic can't be moved into the observer callback because it wouldn't allow the popover to close on dismiss.
    // The `isReferenceVisible` state is used to determine if the popover should open/close based on the `openOnTargetEnter`/`closeOnTargetLeave` props.
    if (openOnTargetEnter && isReferenceVisible) {
      var _onOpenChangeRef$curr2;
      (_onOpenChangeRef$curr2 = onOpenChangeRef.current) === null || _onOpenChangeRef$curr2 === void 0 || _onOpenChangeRef$curr2.call(onOpenChangeRef, syntheticEvent(true));
    } else if (closeOnTargetLeave && !isReferenceVisible) {
      var _onOpenChangeRef$curr3;
      (_onOpenChangeRef$curr3 = onOpenChangeRef.current) === null || _onOpenChangeRef$curr3 === void 0 || _onOpenChangeRef$curr3.call(onOpenChangeRef, syntheticEvent(false));
    }
  }, [openOnTargetEnter, isReferenceVisible, closeOnTargetLeave]);

  /**
   * Adds/removes the `data-popover-id` to the target element if one is provided.
   * If the target prop is used, it becomes the reference, so we want to pass this data attribute to the target instead of the child.
   * The DOM element the Popover is attached to (`target` if given, `children` aka the set domReference element otherwise)
   * We have to directly set the attribute on the targetElement/child because if we pass it as part of the cloneElement call, we can't
   * guarantee that whatever it's cloning will pass it through to the DOM.
   * @see https://git.hubteam.com/HubSpot/foundations-components/issues/6268
   */
  useEffect(() => {
    const targetElement = target || referenceElement;
    // check to make sure that the target element is a DOM element and that it's not any type of
    // node we can't set/remove attributes on i.e a text node
    if (targetElement && targetElement instanceof Element) {
      if (contentOpen) {
        targetElement.setAttribute('data-popover-id', popoverId);
      } else {
        targetElement.removeAttribute('data-popover-id');
      }
    }
  }, [contentOpen, popoverId, target, referenceElement]);

  /**
   * Updates the reference element when the target prop changes.
   */
  useEffect(() => {
    if (target) {
      refs.setPositionReference(target);
    }
  }, [target, refs]);

  // Calls the `onFocusLeave` callback when the focus moves outside of the popover container.
  // We have to do this using native event listeners because the React `onBlur` event doesn't bubble up to the popover container.
  useEffect(() => {
    if (!containerElement || !onFocusLeaveRef.current) {
      return;
    }
    const handleFocusOut = event => {
      // Check if focus is moving to an element outside of the container
      if (!containerElement.contains(event.relatedTarget)) {
        var _onFocusLeaveRef$curr;
        (_onFocusLeaveRef$curr = onFocusLeaveRef.current) === null || _onFocusLeaveRef$curr === void 0 || _onFocusLeaveRef$curr.call(onFocusLeaveRef);
      }
    };
    containerElement.addEventListener('focusout', handleFocusOut);
    return () => {
      containerElement.removeEventListener('focusout', handleFocusOut);
    };
  }, [containerElement]);

  // Updates the transition status based on the open state
  useEffect(() => {
    if (isMounted) {
      if (contentOpen) {
        // If the animation duration is 0, the `onTransitionEnd` event doesn't fire, so we need to set the transition status to `OPEN` here
        setTransitionStatus(isAnimated ? 'OPENING' : 'OPEN');
      } else {
        setTransitionStatus(isAnimated ? 'CLOSING' : 'CLOSED');
      }
    } else {
      setTransitionStatus('CLOSED');
    }
  }, [isMounted, contentOpen, isAnimated]);

  // Calls the open/close complete callbacks when the transition status changes
  useEffect(() => {
    if (transitionStatus === 'OPEN') {
      var _onOpenStartRef$curre, _onOpenCompleteRef$cu;
      // Both open start and open complete callbacks are called at the same time here because `transitionStatus = 'OPEN'` is set after the animation completes (regardless of whether it's animated or not)
      (_onOpenStartRef$curre = onOpenStartRef.current) === null || _onOpenStartRef$curre === void 0 || _onOpenStartRef$curre.call(onOpenStartRef);
      (_onOpenCompleteRef$cu = onOpenCompleteRef.current) === null || _onOpenCompleteRef$cu === void 0 || _onOpenCompleteRef$cu.call(onOpenCompleteRef);
    } else if (transitionStatus === 'CLOSED' && !isFirstRenderRef.current) {
      var _onCloseCompleteRef$c;
      // Switch to toggle animation for all future interactions
      animationSourceRef.current = 'toggle';
      (_onCloseCompleteRef$c = onCloseCompleteRef.current) === null || _onCloseCompleteRef$c === void 0 || _onCloseCompleteRef$c.call(onCloseCompleteRef);
    }
  }, [transitionStatus]);

  // This effect should always be the last one to run
  useEffect(() => {
    isFirstRenderRef.current = false;
  }, []);
  const dismiss = useDismiss(context, {
    enabled: closeOnOutsideClick,
    outsidePressEvent: 'click',
    // Ensures that popover closes when the user clicks (or presses enter/space) outside of the popover
    outsidePress(event) {
      // Ignores outside clicks for elements in this list
      const ignoreElementsList = [...outsideClickIgnoreElementsList, target, referenceElement];
      if (ignoreElementsList.some(element => element === null || element === void 0 ? void 0 : element.contains(event.target))) {
        return false;
      }

      // If `shouldCloseOnClick` is provided and returns a non-null value, defer to it
      if (shouldCloseOnClick) {
        const shouldClose = shouldCloseOnClick(event);
        if (shouldClose != null) {
          return shouldClose;
        }
      }
      return true;
    }
  });
  const {
    getReferenceProps,
    getFloatingProps
  } = useInteractions([dismiss]);
  if (process.env.NODE_ENV !== 'production') {
    const child = children ? Children.only(children) : null;
    if (isFragment(child)) {
      devLogger.warn({
        message: `Popover: child element cannot be a React.Fragment, and must support passing event handlers through to a DOM element.`,
        key: 'Popover: fragment not supported'
      });
    }
  }
  const clonedChild = useMemo(() => {
    const child = children ? Children.only(children) : null;
    if (!child) return null;
    if (target) return child;
    const childProps = child.props;
    return /*#__PURE__*/cloneElement(child, getReferenceProps(Object.assign({}, childProps, {
      onKeyDown: e => {
        var _childProps$onKeyDown;
        if (e.key === 'Escape' && contentOpenRef.current) {
          e.stopPropagation();
        }
        (_childProps$onKeyDown = childProps.onKeyDown) === null || _childProps$onKeyDown === void 0 || _childProps$onKeyDown.call(childProps, e);
      }
    })));
  }, [children, target, getReferenceProps]);
  let popoverContent;
  if (Content) {
    popoverContent = /*#__PURE__*/_jsx(Content, {});
  } else if (typeof content !== 'object' || /*#__PURE__*/isValidElement(content) || Array.isArray(content)) {
    popoverContent = content;
  } else {
    var _content, _content2, _content3, _content4, _content5, _content6, _content7, _content8;
    content = content;
    popoverContent = /*#__PURE__*/_jsxs(_Fragment, {
      children: [(_content = content) !== null && _content !== void 0 && _content.header ? /*#__PURE__*/_jsx(PopoverHeader, {
        showCloseButton: showCloseButton,
        onOpenChange: onOpenChangeRef.current,
        children: content.header
      }) : null, (_content2 = content) !== null && _content2 !== void 0 && _content2.body ? /*#__PURE__*/_jsx(PopoverBody, {
        children: content.body
      }) : null, (((_content3 = content) === null || _content3 === void 0 ? void 0 : _content3.footer) || ((_content4 = content) === null || _content4 === void 0 ? void 0 : _content4.step)) && /*#__PURE__*/_jsxs(PopoverFooter, {
        hasStructuredFooter: !!((_content5 = content) !== null && _content5 !== void 0 && _content5.footer),
        hasStep: !!((_content6 = content) !== null && _content6 !== void 0 && _content6.step),
        children: [(_content7 = content) === null || _content7 === void 0 ? void 0 : _content7.step, (_content8 = content) === null || _content8 === void 0 ? void 0 : _content8.footer]
      })]
    });
  }

  /**
   * "UIPopover" is intentional to provide backwards compatibility with Selenium helpers
   * @see https://git.hubteam.com/HubSpot/foundations-components/issues/3770
   * @see https://git.hubteam.com/HubSpot/hs-selenium/blob/e5e9a447c1c57503507c1ae0fe7ae982c7923f9a/selenium-js-utils/src/UIComponents/popover/internal/core.ts#L14
   */
  const componentName = 'UIPopover';
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [clonedChild && /*#__PURE__*/_jsx(ChildrenWrapper
    // @ts-expect-error - Parameter 'childNode' implicitly has an 'any' type.
    , {
      ref: childNode => {
        /* eslint-disable-next-line react/no-find-dom-node */
        const childElement = findDOMNode(childNode);
        setReferenceElement(childElement);
        if (!target) {
          refs.setReference(childElement);
        }
      },
      children: clonedChild
    }), isMounted ? /*#__PURE__*/_jsx(FloatingPortalWrapper, {
      root: _bodyElement || getDefaultNode(),
      children: /*#__PURE__*/_jsx(StyledFloatingContainer, Object.assign({}, getFloatingProps(Object.assign({}, callbackProps)), {
        id: popoverId,
        ref: node => {
          refs.setFloating(node);
          setContainerElement(node);
        }
        // Necessary for TinyMCE to prevent blur on the editor when component is clicked
        // https://git.hubteam.com/HubSpot/structured-content/issues/702
      }, rteContext, {
        $floatingStyles: floatingStyles,
        $zIndex: zIndex,
        children: /*#__PURE__*/_jsx(FloatingFocusManager, {
          context: context,
          modal: false,
          closeOnFocusOut: false,
          initialFocus: -1,
          returnFocus: false,
          children: /*#__PURE__*/_jsx("div", {
            style: Object.assign({}, animationStyles),
            onTransitionEnd: () => {
              if (isMounted && contentOpen) {
                setTransitionStatus('OPEN');
              }
            },
            children: /*#__PURE__*/_jsx(StyledPopoverContainer, Object.assign({}, domProps, {
              $cssStyles: cssStyles,
              $use: use,
              "data-component-name": componentName,
              "data-open-complete": transitionStatus === 'OPEN' || transitionStatus === 'CLOSING',
              onClick: onClick,
              ref: setPopoverContainerElement,
              children: /*#__PURE__*/_jsxs(PopoverProvider, {
                use: use,
                children: [popoverContent, /*#__PURE__*/_jsx(FloatingArrow, {
                  "data-test-id": "popover-arrow",
                  ref: arrowRef,
                  context: context,
                  fill: computedArrowColor ? theme.color[computedArrowColor] : use === 'default' || use === 'longform' ? theme.color['fill-surface-default'] : use === 'tooltip' ? theme.color['fill-accent-neutral-default'] : use === 'tooltip-danger' ? theme.color['fill-alert-default'] : use === 'shepherd' ? oppositeCurrentPlacementSide === 'top' || currentPlacement === 'left-start' || currentPlacement === 'right-start' ? theme.color['fill-transitional-popover'] : theme.color['fill-surface-default'] : theme.color['fill-transparent-default'],
                  width: arrowSizeDimensions.width,
                  height: arrowSizeDimensions.height,
                  stroke: showArrowStroke ? theme.color['border-transitional-core-subtle'] : undefined,
                  strokeWidth: showArrowStroke ? parseInt(theme.borderWidth['100'], 10) : undefined,
                  tipRadius: use === 'tooltip' || use === 'tooltip-danger' ? 1 : undefined
                })]
              })
            }))
          })
        })
      }))
    }) : undefined]
  });
};
Popover.displayName = 'Popover';